import LegalPage from "@/components/legal/LegalPage";

const sections = [
  {
    id: "overview",
    title: "Overview",
    body: (
      <>
        <p>
          These terms of use (&ldquo;Terms&rdquo;) govern your access to and use
          of the Toulios Wealth Management website at touliosWealth.com (the
          &ldquo;Site&rdquo;) and any associated tools (such as the AI
          Readiness Scorecard). By using the Site, you agree to these Terms.
        </p>
        <p>
          If you do not agree to these Terms, you must not use the Site. These
          Terms are governed by the laws of New South Wales, Australia.
        </p>
      </>
    ),
  },
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <>
        <p>
          The Site is operated by Toulios Wealth Management Pty Ltd (ACN
          667 133 977 · ABN 68 667 133 977), an Australian private company (&ldquo;Toulios&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). Our business
          activity is classified under ANZSIC code{" "}
          <strong className="text-white font-medium">M696299, Management Advice and Related Consulting Services (n.e.c.)</strong>.
        </p>
        <p>
          Toulios provides strategic consulting, management advisory, AI
          consulting, business-scaling and financial-modelling services. We do
          not hold an Australian Financial Services Licence (AFSL), and we do
          not provide brokerage, financial product advice or any other
          regulated financial service. See our{" "}
          <a className="text-gold hover:underline" href="/legal/disclosures">Disclosures</a> page for details.
        </p>
      </>
    ),
  },
  {
    id: "use-of-site",
    title: "Permitted use of the Site",
    body: (
      <>
        <p>You may use the Site for lawful, personal or business purposes. You agree not to:</p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>Use the Site in a way that breaches any law or regulation.</li>
          <li>Attempt to gain unauthorised access to the Site, our infrastructure, or any related systems.</li>
          <li>Interfere with the operation of the Site, including via automated scripts, scraping or denial-of-service activity.</li>
          <li>Reproduce, distribute, or commercialise content from the Site without our written permission.</li>
          <li>Misrepresent your identity or use the Site on behalf of a third party without authority.</li>
        </ul>
      </>
    ),
  },
  {
    id: "information-only",
    title: "Information only, no financial product advice",
    body: (
      <>
        <p>
          The information on this Site is general in nature and is provided for
          information and education purposes only. It does not constitute
          financial product advice, investment advice, a recommendation, an
          offer of any financial product or service, or a solicitation of any
          investment.
        </p>
        <p>
          The Site (including any content, scorecard results, case studies,
          frameworks or commentary) does not take into account your specific
          objectives, financial situation or needs. Before acting on any
          information on the Site, you should consider whether it is
          appropriate for you and seek independent professional advice.
        </p>
      </>
    ),
  },
  {
    id: "past-performance",
    title: "Past performance & track record statements",
    body: (
      <>
        <p>
          Any references to historical portfolio performance, including any
          statements about &ldquo;~36% average annual return 2020–2025 on a
          personal portfolio&rdquo;, relate to a personal portfolio only.
        </p>
        <p>
          Past performance is not indicative of future results, is not a
          promise, and is not representative of client outcomes. References to
          performance are illustrative only and do not constitute a forecast or
          guarantee.
        </p>
      </>
    ),
  },
  {
    id: "engagements",
    title: "Consulting engagements",
    body: (
      <>
        <p>
          Substantive consulting work, including any deliverables, milestones,
          fees, payment terms, confidentiality, intellectual property and
          warranty arrangements, is governed by a separate written engagement
          letter signed by both parties. These Terms do not constitute or
          create such an engagement.
        </p>
      </>
    ),
  },
  {
    id: "fees-billing",
    title: "Fees, billing & engagement terms",
    body: (
      <>
        <p>
          Fees and billing terms are agreed in writing on a per-engagement
          basis, typically as part of the engagement letter. The following
          default arrangements apply unless varied in writing:
        </p>
        <ul className="list-disc list-outside space-y-2 pl-5 marker:text-gold/70">
          <li>
            <strong className="text-white font-medium">Discovery / short-form work</strong>:
            payable at point of sale (POS) before commencement.
          </li>
          <li>
            <strong className="text-white font-medium">Project & retained engagements</strong>:
            invoiced on agreed milestones with payment terms of Net 14 days from
            the invoice date.
          </li>
        </ul>
        <p>
          Fees are quoted exclusive of GST unless otherwise stated. Late
          payment may attract a recovery fee and/or interest at a reasonable
          rate, as set out in the relevant engagement letter. Disbursements
          (e.g., travel, third-party software licences) are charged at cost
          where pre-agreed.
        </p>
      </>
    ),
  },
  {
    id: "scorecard",
    title: "The AI Readiness Scorecard",
    body: (
      <>
        <p>
          The AI Readiness Scorecard is a self-assessment diagnostic tool. The
          score and band you receive are indicative only and depend entirely on
          the inputs you provide. The Scorecard is not financial product advice
          and is not a substitute for a tailored consulting engagement.
        </p>
        <p>
          When you submit the Scorecard, your responses, contact details and
          computed result are stored in accordance with our{" "}
          <a className="text-gold hover:underline" href="/legal/privacy">Privacy Policy</a>.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    title: "Intellectual property",
    body: (
      <>
        <p>
          All content on the Site, including text, graphics, the visual design,
          the Scorecard methodology, frameworks, and trade marks, is owned by
          or licensed to Toulios and is protected by Australian and
          international copyright and trade mark laws.
        </p>
        <p>
          You may not copy, reproduce, modify, distribute, transmit, publicly
          display or otherwise exploit any part of the Site, except as
          permitted by law (for example, fair dealing) or with our prior
          written consent. Where you share materials with us during an
          engagement, you retain ownership and grant us a limited licence to
          use them for the purposes of that engagement.
        </p>
      </>
    ),
  },
  {
    id: "third-party",
    title: "Third-party links",
    body: (
      <>
        <p>
          The Site may include links to third-party websites or services. Such
          links are provided for convenience. We are not responsible for, and
          do not endorse, the content or practices of any third-party site.
          Your access of third-party sites is at your own risk.
        </p>
      </>
    ),
  },
  {
    id: "no-warranty",
    title: "No warranty",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, the Site and its content are
          provided &ldquo;as is&rdquo;, without warranties of any kind, whether
          express or implied. We do not warrant that the Site will be available
          uninterrupted, secure, free of errors or that any information will be
          accurate or complete.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, Toulios is not liable for any
          direct, indirect, incidental, special, consequential or exemplary
          loss or damage arising out of or in connection with your use of, or
          inability to use, the Site or any reliance on its content. Nothing in
          these Terms is intended to exclude, restrict or modify any rights
          you may have under the Australian Consumer Law that cannot be lawfully excluded.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnity",
    body: (
      <>
        <p>
          You agree to indemnify and hold harmless Toulios, its directors,
          employees and contractors from and against any claim, liability,
          loss, damage, cost or expense arising from your breach of these
          Terms or your unlawful or unauthorised use of the Site.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Terms",
    body: (
      <>
        <p>
          We may revise these Terms from time to time. The &ldquo;Last
          updated&rdquo; date at the top of this page will reflect the most
          recent revision. Continued use of the Site after a revision
          constitutes acceptance of the updated Terms.
        </p>
      </>
    ),
  },
  {
    id: "governing-law",
    title: "Governing law & jurisdiction",
    body: (
      <>
        <p>
          These Terms are governed by the laws of New South Wales, Australia.
          You and Toulios submit to the non-exclusive jurisdiction of the
          courts of New South Wales for any matter arising in connection with
          the Site or these Terms.
        </p>
      </>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <p>
          Questions about these Terms? Reach us via the{" "}
          <a className="text-gold hover:underline" href="/contact">Contact</a> page
          or at <span className="text-white">hello@touliosWealth.com</span>.
        </p>
      </>
    ),
  },
];

export default function Terms() {
  return (
    <LegalPage
      eyebrow="Terms of Use"
      title="Terms of Use."
      lastUpdated="20 May 2026"
      summary="The terms that govern your use of this website and its tools, including the AI Readiness Scorecard."
      sections={sections}
    />
  );
}
